// Tracking
import { BUILD_ENVIRONMENTS } from '@src/config/constants'
import { CONFIG_ATTENTIVE_SRC_SCRIPT, CONFIG_BUILD_ENV } from '@src/config/env'
import {
  ATTENTIVE_EMAIL_SUBMIT_EVENT_KEY,
  ATTENTIVE_OVERLAY_ID,
} from '@src/quince-site-common/src/utils/addAttentiveSDK/addAttentiveSdk.constants'
import mixpanelCl from '@src/telemetry/integrations/mixpanel'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

// Utils
import { FEATURES, MODULES } from '../../constants/template-info'
import { getIsNativeViewClient } from '../../helpers/getIsNativeViewClient'
import { ANALYTIC_COOKIES, ATTENTIVE_POP_UP_EXPIRY_MINUTES, FEATURE_COOKIES } from '../constants'
import { captureException } from '../sentryUtil'
import setCookie from '../setCookie'
import { isUserAgentABot } from '../userAgentUtility'
import validateEmail from '../validateEmail'

// Error handling

import { attentiveSdkDlListener, removeSdkDlListener } from './attentiveSdkDlListener'

const getEmailFromDataLayer = (disconnectObserver: () => void) => {
  try {
    const attentiveSubmitEventData = window.dataLayer?.find(d => d?.event === ATTENTIVE_EMAIL_SUBMIT_EVENT_KEY)
    if (attentiveSubmitEventData?.email && validateEmail(attentiveSubmitEventData.email as string)) {
      setCookie(ANALYTIC_COOKIES.LB_CUSTOMER_TENTATIVE_EMAIL, attentiveSubmitEventData.email as string)
      mixpanelCl.identify({ email: attentiveSubmitEventData.email as string })
      disconnectObserver()
    }
  } catch (e) {
    captureException(e, {
      feature: FEATURES.GET_EMAIL_FROM_DATA_LAYER,
      module: MODULES.ATTENTIVE,
      extraInfo: {
        message: 'Failed to get attentive email from window datalayer',
      },
    })
  }
}

function addAttentiveSdk() {
  let muObs: MutationObserver | null = null

  attentiveSdkDlListener()

  const initializeAttentive = () => {
    if (getIsNativeViewClient() || isUserAgentABot || !Object.values(BUILD_ENVIRONMENTS).includes(CONFIG_BUILD_ENV)) {
      return
    }
    try {
      const script = document.createElement('script')
      script.src = CONFIG_ATTENTIVE_SRC_SCRIPT
      script.id = 'attentive'
      document.head.appendChild(script)
      trackEvent(TELEMETRY_EVENT_NAMES.FORM_OPEN, {
        action: 'attentive_popup_open',
      })
      setCookie(FEATURE_COOKIES.IS_ATTENTIVE_POPUP_PREVIOUSLY_OPENED, true, ATTENTIVE_POP_UP_EXPIRY_MINUTES)
      const disconnectObserver = () => {
        if (muObs) {
          muObs.disconnect()
        }
      }
      let isPopupOpened = false
      // eslint-disable-next-line no-undef
      muObs = new MutationObserver(() => {
        const el = document.getElementById(ATTENTIVE_OVERLAY_ID)
        if (el && !isPopupOpened) {
          isPopupOpened = true
        }
        if (!el && isPopupOpened) {
          getEmailFromDataLayer(disconnectObserver)
          removeSdkDlListener()
        }
      })
      // eslint-disable-next-line no-undef
      muObs.observe(document, {
        childList: true, // observe direct children
        subtree: true, // and lower descendants too
        characterDataOldValue: true, // pass old data to callback
      })
    } catch (error) {
      captureException(error, {
        feature: FEATURES.LOAD_SDK,
        module: MODULES.ATTENTIVE,
      })
    }
  }

  return { initializeAttentive }
}

export default addAttentiveSdk
